import { logE, log, logV } from "../../shared/services/debug-logger";
import {
  CreateHullCleaningRequestResponse,
  HULL_CLEANING_REQUEST_REASONS,
  HullCleaningRequestFormErrors,
  HullCleaningRequestFormValue,
  HullCleaningRequestReason,
} from "../models/hull-cleaning.models";
import { createHullCleaningRequest } from "../services/hull-cleaning.service";
import {
  EMPTY_HULL_CLEANING_REQUEST_FORM,
  getHullCleaningRequestErrorMessage,
  normalizeHullCleaningRequestForm,
  validateHullCleaningRequestForm,
} from "../utils/hull-cleaning-request.utils";
import { Dialog } from "@headlessui/react";
import { ArrowPathIcon, CheckIcon, ExclamationTriangleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { FormEvent, useEffect, useRef, useState } from "react";

interface Props {
  open: boolean;
  imoNumber: string;
  onCancel: () => void;
  onCreated: (response: CreateHullCleaningRequestResponse) => void;
}

const inputClassName =
  "h-11 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 shadow-sm focus:border-green focus:outline-none focus:ring-2 focus:ring-green/20 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:disabled:bg-gray-700";

const initialForm = (imoNumber: string): HullCleaningRequestFormValue => ({
  ...EMPTY_HULL_CLEANING_REQUEST_FORM,
  imoNumber,
});

const HullCleaningRequestModal = ({ open, imoNumber, onCancel, onCreated }: Props) => {
  const [form, setForm] = useState<HullCleaningRequestFormValue>(() => initialForm(imoNumber));
  const [errors, setErrors] = useState<HullCleaningRequestFormErrors>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const reasonRef = useRef<HTMLSelectElement>(null);

  const reset = () => {
    setForm(initialForm(imoNumber));
    setErrors({});
    setApiError(null);
  };

  useEffect(() => {
    logV("HullCleaningRequestModal - visibility", { open });
    if (open) reset();
    // Resetting is intentionally tied to dialog visibility and vessel context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, imoNumber]);

  const updateField = <K extends keyof HullCleaningRequestFormValue>(
    field: K,
    value: HullCleaningRequestFormValue[K],
  ) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
    setApiError(null);
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;

    const nextErrors = validateHullCleaningRequestForm(form);
    log("HullCleaningRequestModal - submit", { validationErrors: Object.keys(nextErrors).length });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setIsSubmitting(true);
    setApiError(null);
    try {
      const response = await createHullCleaningRequest(normalizeHullCleaningRequestForm(form));
      reset();
      log("HullCleaningRequestModal - submit - created");
      onCreated(response);
    } catch (error) {
      logE(new Error("HullCleaningRequestModal - submit - failed"));
      setApiError(getHullCleaningRequestErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  logV("HullCleaningRequestModal - BUILD", { open, submitting: isSubmitting });

  return (
    <Dialog
      open={open}
      onClose={() => !isSubmitting && onCancel()}
      initialFocus={reasonRef}
      className="relative z-[1000]"
    >
      <div className="fixed inset-0 bg-slate-950/55 backdrop-blur-[2px]" aria-hidden="true" />
      <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6">
        <div className="flex min-h-full items-center justify-center">
          <Dialog.Panel
            as="form"
            onSubmit={submit}
            data-test="hull-cleaning-request-form"
            className="w-full max-w-2xl overflow-hidden rounded-2xl border border-[#D7E2DE] bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800"
          >
            <div className="border-greyscale-20 flex items-start justify-between gap-4 border-b bg-[#F7FBFA] px-5 py-5 dark:border-gray-700 dark:bg-gray-800 sm:px-6">
              <div>
                <Dialog.Title className="text-xl font-semibold text-gray-900 dark:text-white">
                  Create Hull Cleaning Request
                </Dialog.Title>
                <Dialog.Description className="mt-1 text-sm text-gray-500 dark:text-gray-300">
                  Submit a request for the current vessel. Vessel details and recipient routing are resolved by OpsLink.
                </Dialog.Description>
              </div>
              <button
                type="button"
                onClick={onCancel}
                disabled={isSubmitting}
                aria-label="Close hull cleaning request dialog"
                className="border-greyscale-20 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border bg-white text-gray-500 transition-colors hover:bg-greyscale-10 hover:text-gray-900 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {apiError && (
              <div
                role="alert"
                className="mx-5 mt-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200 sm:mx-6"
              >
                <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" />
                <span>{apiError}</span>
              </div>
            )}

            <div className="grid grid-cols-1 gap-5 px-5 py-6 sm:px-6 md:grid-cols-2">
              <Field label="IMO Number" error={errors.imoNumber}>
                <input
                  required
                  readOnly
                  inputMode="numeric"
                  maxLength={7}
                  value={form.imoNumber}
                  aria-invalid={Boolean(errors.imoNumber)}
                  aria-describedby={errors.imoNumber ? "hull-cleaning-imo-number-error" : undefined}
                  className={inputClassName}
                  data-test="hull-cleaning-request-imo"
                />
              </Field>

              <Field label="Reason" error={errors.reason}>
                <select
                  ref={reasonRef}
                  required
                  value={form.reason}
                  onChange={(event) => updateField("reason", event.target.value as HullCleaningRequestReason | "")}
                  aria-invalid={Boolean(errors.reason)}
                  aria-describedby={errors.reason ? "hull-cleaning-reason-error" : undefined}
                  className={inputClassName}
                  data-test="hull-cleaning-request-reason"
                >
                  <option value="">Select a reason</option>
                  {HULL_CLEANING_REQUEST_REASONS.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
              </Field>

              <div className="md:col-span-2">
                <Field label="Comment" error={errors.comment}>
                  <textarea
                    required
                    rows={5}
                    value={form.comment}
                    onChange={(event) => updateField("comment", event.target.value)}
                    aria-invalid={Boolean(errors.comment)}
                    aria-describedby={errors.comment ? "hull-cleaning-comment-error" : undefined}
                    placeholder="Describe the reason for this request"
                    className="min-h-[120px] w-full resize-y rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm focus:border-green focus:outline-none focus:ring-2 focus:ring-green/20 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                    data-test="hull-cleaning-request-comment"
                  />
                </Field>
              </div>
            </div>

            <div className="border-greyscale-20 flex flex-col-reverse gap-3 border-t bg-gray-50 px-5 py-4 dark:border-gray-700 dark:bg-gray-900/40 sm:flex-row sm:justify-end sm:px-6">
              <button
                type="button"
                onClick={reset}
                disabled={isSubmitting}
                className="inline-flex h-11 items-center justify-center rounded-md border border-gray-300 bg-white px-5 text-sm font-semibold text-lightGrey transition-colors hover:bg-greyscale-10 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
              >
                Clear
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-green px-5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#176a5e] disabled:cursor-not-allowed disabled:opacity-50"
                data-test="hull-cleaning-request-submit"
              >
                {isSubmitting ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckIcon className="h-4 w-4" />}
                {isSubmitting ? "Submitting..." : "Submit request"}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};

const Field = ({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) => {
  const errorId = `hull-cleaning-${label.toLowerCase().replaceAll(" ", "-")}-error`;
  return (
    <label className="flex flex-col gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
      <span>
        {label}
        <sup className="ml-1 text-[#BF0002]" aria-hidden="true">
          *
        </sup>
      </span>
      {children}
      {error && (
        <span id={errorId} className="text-xs font-normal text-red-600 dark:text-red-300">
          {error}
        </span>
      )}
    </label>
  );
};

export default HullCleaningRequestModal;
